import { useState } from 'react';
import type { StartupStage } from '@shared/types';

const STAGES = [
  { key: 'idea',       icon: '', label: 'Idea Validated' },
  { key: 'validation', icon: '', label: 'Building MVP' },
  { key: 'mvp',        icon: '', label: 'Beta Testing' },
  { key: 'growth',     icon: '', label: 'Revenue' },
  { key: 'scale',      icon: '', label: 'Scale' },
];

const STAGE_INDEX: Record<StartupStage, number> = {
  idea: 0, validation: 1, mvp: 2, growth: 3,
};

interface Props {
  stage: StartupStage;
}

export function StageLifecycleBar({ stage }: Props) {
  const currentIdx = STAGE_INDEX[stage] ?? 0;
  const [tooltip, setTooltip] = useState<number | null>(null);

  return (
    <>
      {/* Desktop: full connected bar */}
      <div className="hidden sm:block" style={{
        background: '#161627', border: '1px solid #2a2a3e',
        borderRadius: 12, padding: '14px 20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
          {STAGES.map((s, i) => {
            const isPast = i < currentIdx;
            const isCurrent = i === currentIdx;
            const isFuture = i > currentIdx;

            const circleColor = isCurrent ? '#7c6ff7' : isPast ? '#22c55e' : '#1e1e30';
            const circleBorder = isCurrent ? '#7c6ff7' : isPast ? '#22c55e' : '#2a2a3e';
            const labelColor = isCurrent ? '#fff' : isPast ? '#9ca3af' : '#4b5563';

            return (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', flex: i < STAGES.length - 1 ? 1 : 0 }}>
                {/* Node */}
                <div
                  style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: isFuture || isPast ? 'pointer' : 'default', flexShrink: 0 }}
                  onMouseEnter={() => (isFuture || isPast) && setTooltip(i)}
                  onMouseLeave={() => setTooltip(null)}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: circleColor,
                    border: `2px solid ${circleBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: isCurrent ? '0 0 0 4px rgba(124,111,247,0.2)' : 'none',
                  }}>
                    {isPast
                      ? <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>✓</span>
                      : <span style={{ fontSize: 14 }}>{s.icon}</span>
                    }
                  </div>
                  <span style={{
                    fontSize: 11, marginTop: 5, color: labelColor,
                    fontWeight: isCurrent ? 700 : 400, whiteSpace: 'nowrap',
                  }}>{s.label}</span>

                  {/* Tooltip */}
                  {tooltip === i && (
                    <div style={{
                      position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                      marginBottom: 8, background: '#1e1e30', border: '1px solid #2a2a3e',
                      borderRadius: 6, padding: '5px 10px', fontSize: 11, color: '#d1d5db',
                      whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none',
                    }}>
                      {isFuture
                        ? `Complete ${STAGES[currentIdx].label} to unlock`
                        : `${s.label} milestone`
                      }
                    </div>
                  )}
                </div>

                {/* Connector line */}
                {i < STAGES.length - 1 && (
                  <div style={{ flex: 1, height: 2, margin: '0 6px', marginBottom: 22, background: isPast ? '#22c55e40' : '#2a2a3e', borderRadius: 1 }}>
                    {isPast && <div style={{ height: '100%', background: '#22c55e', borderRadius: 1 }} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: condensed single-line */}
      <div className="flex sm:hidden" style={{
        background: '#161627', border: '1px solid #2a2a3e', borderRadius: 10, padding: '12px 16px',
      }}>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>
              {STAGES[currentIdx].icon} Stage {currentIdx + 1} of {STAGES.length}: {STAGES[currentIdx].label}
            </span>
            <span style={{ fontSize: 11, color: '#6b7280' }}>→</span>
          </div>
          <div style={{ height: 4, background: '#1e1e30', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2,
              width: `${((currentIdx + 1) / STAGES.length) * 100}%`,
              background: 'linear-gradient(90deg, #22c55e, #7c6ff7)',
              transition: 'width 0.6s ease',
            }} />
          </div>
        </div>
      </div>
    </>
  );
}
